import { useState, useEffect, useCallback } from 'react'
import { api } from '@/config/api'
import type {
  ThreadSummary,
  ThreadInfo,
  ThreadWithMessages,
  ThreadListResponse,
  MessagesResponse,
  RecentChatsResponse,
  Message,
  Pagination,
} from '@/types'

export function useThreads(userId: string, page = 1, limit = 20, isActive?: boolean) {
  const [threads, setThreads] = useState<ThreadSummary[]>([])
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params: Record<string, unknown> = { page, limit }
      if (isActive !== undefined) params.is_active = isActive

      const { data } = await api.get<ThreadListResponse>(
        `/api/users/${userId}/threads`,
        { params }
      )
      setThreads(data.threads)
      setPagination(data.pagination)
    } catch (err) {
      setError((err as Error).message)
      console.error('Failed to fetch threads:', err)
    } finally {
      setLoading(false)
    }
  }, [userId, page, limit, isActive])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { threads, pagination, loading, error, refetch }
}

export function useMessages(userId: string, threadId: number, page = 1, limit = 100) {
  const [thread, setThread] = useState<ThreadInfo | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get<MessagesResponse>(
        `/api/users/${userId}/threads/${threadId}/messages`,
        { params: { page, limit } }
      )
      setThread(data.thread)
      setMessages(data.messages)
      setPagination(data.pagination)
    } catch (err) {
      setError((err as Error).message)
      console.error('Failed to fetch messages:', err)
    } finally {
      setLoading(false)
    }
  }, [userId, threadId, page, limit])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { thread, messages, pagination, loading, error, refetch }
}

export function useRecentChats(userId: string, days = 7) {
  const [data, setData] = useState<RecentChatsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get<RecentChatsResponse>(
        `/api/users/${userId}/recent-chats`,
        { params: { days } }
      )
      setData(data)
    } catch (err) {
      setError((err as Error).message)
      console.error('Failed to fetch recent chats:', err)
    } finally {
      setLoading(false)
    }
  }, [userId, days])

  useEffect(() => {
    refetch()
  }, [refetch])

  return {
    threads: data?.threads ?? [],
    period: data?.period,
    totalMessages: data?.total_messages ?? 0,
    loading,
    error,
    refetch,
  }
}

// Legacy function for backwards compatibility - fetches all threads with their messages
export function useThreadsWithMessages(userId: string) {
  const [threads, setThreads] = useState<ThreadWithMessages[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Use recent-chats endpoint which returns threads with messages
      const { data } = await api.get<RecentChatsResponse>(
        `/api/users/${userId}/recent-chats`,
        { params: { days: 30 } }
      )
      setThreads(data.threads)
    } catch (err) {
      setError((err as Error).message)
      console.error('Failed to fetch threads with messages:', err)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { threads, loading, error, refetch }
}
